import {
  DueDisplayOptions,
  Habit,
  ID,
  initialDateFormat,
  LinkOptions,
  ScheduleEntryState,
  Task,
  Timestamp,
  UpdateTask,
} from '@griply/types';
import { getWeekStartsOn, isNumber } from '@griply/utils';
import { isBefore, isSameDay, startOfDay } from 'date-fns';
import { useCreateUnlimitedGoalsAllowed, useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import {
  useTaskTodayControls,
  useUpdateHabitScheduleEntry,
} from 'features/tasks';
import React, { memo } from 'react';
import { TaskMetaInteractionDueDate } from 'shared/components/ui/task-meta-interaction-due-date';
import { TaskMetaInteractionLinkedItem } from 'shared/components/ui/task-meta-interaction-linked-item';
import { TaskMetaInteractionTimeslot } from 'shared/components/ui/task-meta-interaction-timeslot';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useTaskPlannedOccurrenceForDate } from 'shared/hooks/use-task-planned-occurrence-for-date';
import { useUser } from 'shared/hooks/use-user';
import { getScheduleCompletedOnDate } from 'shared/utils/get-schedule-completed-on-date';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { shouldDueBeShown } from 'shared/utils/should-due-be-shown';

import { TaskMetaInteractionTags } from './task-meta-interaction-tags';
import * as Styled from './task-meta-interactions.style';
import { useFutureOccurrence } from './use-future-occurrence';

const allDueDisplayOptions = Object.values(DueDisplayOptions);

export type TaskMetaInteractionsProps = {
  task: Task;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
  displayDate?: Timestamp;
  displayDue?: DueDisplayOptions[];
  hasTooltips?: boolean;
  hideGoal?: boolean;
  hideLifeArea?: boolean;
  showGoalColor?: boolean;
  highlight?: boolean;
  preventInteraction?: boolean;
  isMobileOutlined?: boolean;
  isFaded?: boolean;
  tagIds?: ID[];
  autoWidth?: boolean;
};

export const TaskMetaInteractions: React.FC<TaskMetaInteractionsProps> = memo(
  ({
    task,
    onUpdate,
    displayDate,
    displayDue = allDueDisplayOptions,
    hideGoal,
    hideLifeArea,
    showGoalColor,
    highlight,
    hasTooltips,
    preventInteraction,
    isMobileOutlined,
    isFaded,
    tagIds,
    autoWidth,
  }) => {
    const user = useUser();
    const today = useToday();
    const goals = useGoals();
    const lifeAreas = useLocalizedLifeAreas();

    const { allowed: createGoalAllowed } = useCreateUnlimitedGoalsAllowed();

    const openPremiumDialog = useOpenPremiumDialog();
    const openCreateGoalDialog = useOpenCreateGoalDialog();

    const { onUpdate: onUpdateGlobal } = useTaskTodayControls();
    const handleUpdate = onUpdate ?? onUpdateGlobal;

    const updateEntry = useUpdateHabitScheduleEntry();

    const onCreateGoal = (name?: string) =>
      createGoalAllowed ? openCreateGoalDialog({ name }) : openPremiumDialog();

    const activeSchedule = useActiveSchedule(task.schedules, today);

    // prevent edit mode on the task-card
    const onItemContainer = (e: React.MouseEvent) => e.stopPropagation();

    const updateDeadline = (date?: Timestamp) => {
      if (task.frozenAt) {
        return;
      }

      handleUpdate(task, {
        id: task.id,
        endStrategy: {
          deadline: date ?? null,
          completionCount: task.endStrategy?.completionCount ?? null,
        },
      });
    };

    const updateSkip = (date?: Timestamp) => {
      if (!!task.frozenAt || !date) {
        return;
      }

      updateEntry(task as Habit, date, ScheduleEntryState.Skip);
    };

    const onChangeGoal = (link: { id: ID; type: LinkOptions } | null) =>
      !task.frozenAt &&
      handleUpdate(task, {
        id: task.id,
        lifeAreaId: link?.type === LinkOptions.LifeArea ? link.id : null,
        goalId: link?.type === LinkOptions.Goal ? link.id : null,
      });

    const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
    const weekStartsOn = getWeekStartsOn(user);

    const plannedOccurrenceForDate = useTaskPlannedOccurrenceForDate(
      task,
      displayDate ?? today,
      { weekStartsOn, today },
    );

    const plannedOccurrence = React.useMemo(() => {
      if (!plannedOccurrenceForDate || !activeSchedule) {
        return plannedOccurrenceForDate;
      }

      const isCompleted = getScheduleCompletedOnDate(
        activeSchedule,
        plannedOccurrenceForDate.date,
        {
          fullyCompleted: true,
        },
      );

      if (isCompleted) {
        return undefined;
      }

      const isPlannedOccurrenceInActiveSchedule =
        activeSchedule.plannedOccurrences?.find((occurrence) =>
          isSameDay(occurrence.date, plannedOccurrenceForDate.date),
        );

      if (isPlannedOccurrenceInActiveSchedule) {
        return plannedOccurrenceForDate;
      }

      if (
        activeSchedule.startDate &&
        (isSameDay(
          plannedOccurrenceForDate.date,
          startOfDay(activeSchedule.startDate),
        ) ||
          !isBefore(
            plannedOccurrenceForDate.date,
            startOfDay(activeSchedule.startDate),
          ))
      ) {
        return plannedOccurrenceForDate;
      }

      return undefined;
    }, [activeSchedule, plannedOccurrenceForDate]);

    const futureOccurrence = useFutureOccurrence(task, {
      plannedOccurrence,
      displayDue,
      displayDate,
      today,
      weekStartsOn,
    });
    const plannedOccurrenceShown =
      plannedOccurrence?.date ?? futureOccurrence?.date;
    const fallbackDeadline =
      !plannedOccurrenceShown &&
      !task.schedules?.length &&
      task.endStrategy?.deadline
        ? task.endStrategy.deadline
        : undefined;

    const dueDate =
      (plannedOccurrenceShown &&
        shouldDueBeShown(plannedOccurrenceShown, displayDue, today)) ||
      (fallbackDeadline &&
        shouldDueBeShown(fallbackDeadline, displayDue, today))
        ? plannedOccurrence?.date ?? futureOccurrence?.date ?? fallbackDeadline
        : undefined;
    const timeslot = plannedOccurrence
      ? plannedOccurrence.timeslot
      : activeSchedule
        ? activeSchedule.timeslot
        : task.timeslot;

    const hasTimeslot =
      !!timeslot && (isNumber(timeslot.startTime) || !!timeslot.duration);

    const linkedItemId =
      (hideGoal && task.goalId) || (hideLifeArea && task.lifeAreaId)
        ? undefined
        : task.goalId ?? task.lifeAreaId;

    return (
      <Styled.List
        $preventInteraction={!!preventInteraction}
        $isMobileOutlined={!!isMobileOutlined}
        $isFaded={!!isFaded}
        $autoWidth={!!autoWidth}
      >
        {(!!dueDate || hasTimeslot) && (
          <Styled.ListItem
            onClick={onItemContainer}
            $hasValue={!!dueDate || hasTimeslot}
            $isMobileOutlined={!!isMobileOutlined}
          >
            {!!dueDate && (
              <TaskMetaInteractionDueDate
                dateFormat={dateFormat}
                weekStartsOn={weekStartsOn}
                onChange={task.schedules ? updateSkip : updateDeadline}
                isFrozen={!!task.frozenAt}
                value={dueDate}
                hasTimeslot={hasTimeslot}
                highlight={highlight}
                hasTooltip={hasTooltips}
                isDeadline={!task.schedules}
                canRemove={!task.schedules}
                isMobileOutlined={isMobileOutlined}
                tomorrowLabelAllowed
                isCompleted={!!task.completedAt}
              />
            )}
            {hasTimeslot && (
              <TaskMetaInteractionTimeslot
                task={task}
                timeFormat={getTimeFormat(user)}
                isFrozen={!!task.frozenAt}
                value={timeslot}
                representedDay={displayDate ?? today}
                hasDueDate={!!dueDate}
                hasTooltip={hasTooltips}
                isMobileOutlined={isMobileOutlined}
              />
            )}
          </Styled.ListItem>
        )}

        {!!linkedItemId && (
          <Styled.ListItem
            onClick={onItemContainer}
            $hasValue={!!linkedItemId}
            $isMobileOutlined={!!isMobileOutlined}
          >
            <TaskMetaInteractionLinkedItem
              goals={goals}
              lifeAreas={lifeAreas}
              value={linkedItemId}
              onChange={onChangeGoal}
              isFrozen={!!task.frozenAt}
              onCreate={onCreateGoal}
              showGoalColor={showGoalColor}
              highlight={highlight}
              hasTooltip={hasTooltips}
              isMobileOutlined={isMobileOutlined}
            />
          </Styled.ListItem>
        )}

        <TaskMetaInteractionTags
          id={task.id}
          isMobileOutlined={isMobileOutlined}
          tagIds={tagIds}
        />
      </Styled.List>
    );
  },
);
